import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useApplicationModals } from "app/common/hooks";
import { useBarcodeScanner } from "app/context/BarcodeScannerContext";
import { Badge } from "app/components/atoms/badge";
import { BadgeButton, Button, IconButton } from "app/components/atoms/button";
import { InputLabel } from "app/components/atoms/form";
import { Input } from "app/components/atoms/input";
import { FormHeader, FormSummary } from "app/components/atoms/layout";
import Table from "app/components/atoms/table";
import api from "app/integration/api";
import { useConfirm } from "app/components/molecules/confirm";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { isUniqueBarcode } from "app/common/product";

const breadcrumbItems = [
  { label: "Gudang", href: "#" },
  { label: "Transfer Gudang", href: "/warehouse-transfer" },
  { label: "Detail Transfer", href: "#" },
];

const statusLabels = {
  draft: "Draft",
  in_transit: "Dalam Perjalanan",
  received: "Diterima",
  canceled: "Dibatalkan",
};

const statusBadgeColor = (status) => {
  switch (status) {
    case "draft":
      return "gray";
    case "in_transit":
      return "blue";
    case "received":
      return "green";
    case "canceled":
      return "red";
    default:
      return "gray";
  }
};

const warehouseLabel = (id, warehouses) =>
  id == null
    ? "Gudang Utama"
    : warehouses.find((w) => w.id === id)?.name ?? "Gudang #" + id;

/**
 * Satu entri unit fisik untuk daftar barcode di baris yang diperluas.
 *
 * Barisnya SELALU dibuat, termasuk saat relasi `product_code` tidak terbawa.
 * Relasi itu di-include sebagai LEFT JOIN dan `product_codes` bersifat
 * paranoid, jadi unit yang product code-nya sudah di-soft-delete datang
 * dengan `product_code` null — kalau baris seperti itu dilewati, unitnya
 * tetap dihitung di kolom Qty tapi hilang dari daftar "N Unit" dan tidak bisa
 * dihapus satuan, hanya lewat hapus grup.
 *
 * @param {Object} detail - Baris detail transfer gudang.
 * @returns {{ id: number|string, qr_code: string }} Entri unit untuk ditampilkan.
 */
const toUnitCode = (detail) => ({
  id: detail?.id,
  qr_code: detail?.product_code?.qr_code ?? "-",
});

function WarehouseTransferDetail() {
  const { confirm, ConfirmDialog } = useConfirm({ testId: "warehouse-transfer-detail-confirm" });
  const { id } = useParams();
  const { openModal, setPayloads, showStates } = useApplicationModals();

  const [warehouseTransfer, setWarehouseTransfer, , rawFetch] = useFetcher({
    modelName: "warehouseTransfer",
    payload: { id },
  });

  const [warehouses, setWarehouses] = useState([]);
  const [qrCode, setQrCode] = useState("");
  const [isAddingUnit, setIsAddingUnit] = useState(false);
  const actionQueueRef = useRef(Promise.resolve());
  const [savingDetailId, setSavingDetailId] = useState(null);
  const [deletingDetailId, setDeletingDetailId] = useState(null);
  // Dipisah dari deletingDetailId: `group.id` adalah id detail unit PERTAMA,
  // jadi state yang sama membuat penghapusan satu unit menyalakan "Menghapus..."
  // di sel aksi grup — seolah seluruh grup yang sedang dibuang.
  const [deletingUnitId, setDeletingUnitId] = useState(null);
  const [isShipping, setIsShipping] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  /**
   * Mengubah status ekspansi baris grup untuk menampilkan atau menyembunyikan daftar barcode unit unik.
   *
   * @param {number|string} rowKey - Kunci grup/varian yang ingin di-expand atau di-collapse
   * @returns {void} Tidak mengembalikan nilai
   * @sideEffects
   * - Memperbarui state expandedRows dengan membalik status boolean baris tersebut
   */
  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const details = useMemo(() => warehouseTransfer?.details ?? [], [warehouseTransfer?.details]);
  const status = warehouseTransfer?.status;
  const isDraft = status === "draft";

  const detailsRef = useRef(details);
  detailsRef.current = details;

  /**
   * Mengambil data transfer gudang terbaru dan secara sinkron memperbarui
   * detailsRef.current sebelum microtask antrean berikutnya berjalan.
   * Menjamin queued actions selalu membaca baris detail paling mutakhir (anti stale-closure).
   *
   * @returns {Promise<Object>} Objek data transfer gudang terbaru
   * @sideEffects
   * - Memanggil API api.warehouseTransfer.show(id)
   * - Memperbarui state warehouseTransfer via setWarehouseTransfer
   * - Menyelaraskan detailsRef.current secara instan
   * - Memanggil rawFetch sebagai fallback jika terjadi kegagalan
   */
  const fetch = async () => {
    try {
      const res = await api.warehouseTransfer.show(id);
      const freshData = res?.data ?? res;
      if (freshData) {
        setWarehouseTransfer(freshData);
        detailsRef.current = freshData?.details ?? [];
      }
      return freshData;
    } catch (err) {
      return await rawFetch();
    }
  };

  const showError = (title, err, fallback) => {
    setPayloads("errorMessage.view", {
      title,
      message: err?.response?.data?.message ?? fallback ?? "Terjadi kesalahan",
    });
    openModal("errorMessage.view");
  };

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
        showError("Transfer Gudang", err);
      }
    };
    loadWarehouses();
  }, []);

  /**
   * Mengelompokkan detail transfer gudang untuk tampilan tabel.
   * - Unit fisik unik (product_code_id != null) dengan varian yang sama digabung ke dalam satu baris (group).
   *   Kuantitas diakumulasikan, dan daftar barcode unit disimpan pada unitCodes.
   * - Varian umum (product_code_id == null) ditampilkan per item dan kuantitasnya dapat diedit langsung.
   *
   * @type {Array<Object>}
   */
  const groupedDetails = useMemo(() => {
    const groups = [];
    const uniqueMap = new Map();

    for (const detail of details) {
      const isUnique = Boolean(detail?.product_code_id);
      const variantId = detail?.product_variant_id;

      if (isUnique) {
        if (uniqueMap.has(variantId)) {
          const group = uniqueMap.get(variantId);
          group.qty += Number(detail?.qty || 1);
          group.detailIds.push(detail?.id);
          group.unitCodes.push(toUnitCode(detail));
        } else {
          const newGroup = {
            id: detail?.id,
            detailIds: [detail?.id],
            product_variant_id: variantId,
            product_code_id: detail?.product_code_id,
            variant: detail?.variant,
            qty: Number(detail?.qty || 1),
            isUnique: true,
            unitCodes: [toUnitCode(detail)],
            rawDetail: detail,
          };
          uniqueMap.set(variantId, newGroup);
          groups.push(newGroup);
        }
      } else {
        groups.push({
          id: detail?.id,
          detailIds: [detail?.id],
          product_variant_id: variantId,
          product_code_id: null,
          variant: detail?.variant,
          qty: Number(detail?.qty || 0),
          isUnique: false,
          unitCodes: [],
          rawDetail: detail,
        });
      }
    }

    return groups;
  }, [details]);

  /**
   * Menjadwalkan aksi mutasi transfer gudang ke dalam antrean serial terpadu.
   * Menjamin operasi async (pemindaian barcode hardware maupun penambahan via modal)
   * dieksekusi berurutan tanpa race condition, serta mencegah scan hilang (zero dropped scans).
   *
   * @param {() => Promise<void>} actionFn - Fungsi asinkron yang melakukan mutasi detail transfer.
   * @returns {Promise<void>}
   */
  const enqueueTransferAction = (actionFn) => {
    actionQueueRef.current = actionQueueRef.current
      .then(actionFn)
      .catch((err) => {
        console.error("[WarehouseTransfer] Queue error:", err);
      });
    return actionQueueRef.current;
  };

  /**
   * Memastikan status pelacakan barcode unik (is_unique_barcode) untuk varian atau produk.
   * Jika nilai tidak tersedia pada relasi bawaan (respons list ramping), melakukan fallback
   * query ke api.product.show untuk menjamin ketepatan identifikasi tanpa asumsi struktur respons.
   *
   * @param {Object} [variant] - Objek varian produk.
   * @param {Object} [product] - Objek produk master (opsional jika sudah ada).
   * @returns {Promise<boolean>} True jika item dilacak per unit fisik unik.
   */
  const checkIsUniqueBarcode = async (variant, product = null) => {
    let isUniqueVal =
      product?.is_unique_barcode ??
      variant?.product_detail?.is_unique_barcode ??
      variant?.product?.is_unique_barcode;

    if (isUniqueVal === undefined) {
      const productId = product?.id || variant?.product_id;
      if (productId) {
        try {
          const res = await api.product.show(productId);
          const fetchedProduct = res?.data?.data || res?.data;
          isUniqueVal = fetchedProduct?.is_unique_barcode;
        } catch (err) {
          console.warn("[WarehouseTransfer] Gagal mengambil detail produk untuk verifikasi barcode unik:", err);
        }
      }
    }

    return isUniqueBarcode(isUniqueVal);
  };

  /**
   * Menangani penambahan item ke transfer gudang dari modal pencarian produk ("Pilih Item").
   * Jika varian umum tersebut sudah ada di daftar transfer (diperiksa via detailsRef),
   * kuantitasnya akan bertambah 1. Jika belum ada, membuat baris detail baru dengan kuantitas 1.
   * Menjamin anti stale-closure pada antrean eksekusi dan memverifikasi keunikan produk secara akurat.
   *
   * @param {Object} payload - Objek varian terpilih dari modal produk
   * @param {Object} [payload.product] - Objek produk master
   * @param {Object} payload.variant - Objek detail varian
   * @param {number} payload.variant.id - ID varian produk
   * @returns {Promise<void>} Tidak mengembalikan nilai
   * @sideEffects
   * - Menjadwalkan aksi ke antrean serial actionQueueRef
   * - Membaca data mutakhir dari detailsRef.current untuk evaluasi eksistensi detail
   * - Memanggil API api.warehouseTransfer.updateDetail jika varian sudah ada
   * - Memanggil API api.warehouseTransfer.addDetail jika varian belum ada
   * - Memanggil fetch() untuk memuat ulang data transfer gudang dan menyelaraskan detailsRef
   * - Membuka modal error jika terjadi kegagalan request atau item merupakan produk unik
   */
  const handleSelectItem = async (payload) => {
    const productVariantId = payload?.variant?.id;
    if (!productVariantId) return;

    const isUnique = await checkIsUniqueBarcode(payload?.variant, payload?.product);
    if (isUnique) {
      showError(
        "Pilih Item Gagal",
        null,
        "Produk ini menggunakan barcode unik. Silakan tambahkan unit fisik satu per satu melalui scanner barcode."
      );
      return;
    }

    return enqueueTransferAction(async () => {
      try {
        const existingDetail = detailsRef.current.find(
          (d) => d.product_variant_id === productVariantId && !d.product_code_id
        );

        if (existingDetail) {
          await api.warehouseTransfer.updateDetail(id, existingDetail.id, {
            qty: Number(existingDetail.qty) + 1,
          });
        } else {
          await api.warehouseTransfer.addDetail(id, {
            product_variant_id: productVariantId,
            qty: 1,
          });
        }
        await fetch();
      } catch (err) {
        showError("Transfer Gudang", err, "Gagal menambahkan item.");
      }
    });
  };

  /**
   * Menangani penambahan barang ke transfer gudang melalui scan barcode / QR Code.
   * Mendukung pemanggilan dari submit form input maupun scanner barcode hardware global.
   * Mengalirkan pemindaian ke antrean serial terpadu untuk mencegah race condition dan scan hilang,
   * membaca detailsRef untuk mencegah duplikasi unit fisik serta stale-closure pada varian umum,
   * serta memvalidasi keunikan barcode dengan aman tanpa false-positive pada barcode tak dikenal.
   *
   * @param {React.FormEvent<HTMLFormElement>|string} [eOrBarcode] - Event form submit atau string barcode hasil pemindaian.
   * @returns {Promise<void>} Tidak mengembalikan nilai
   * @sideEffects
   * - Menjadwalkan aksi ke antrean serial actionQueueRef
   * - Mengubah state isAddingUnit
   * - Membaca detailsRef.current untuk mencegah scan ganda unit fisik dan duplikasi baris varian
   * - Memanggil API productCode.index, productVariant.index, atau product.index
   * - Memanggil API addDetail atau updateDetail
   * - Mengosongkan state qrCode (baik berhasil maupun gagal)
   * - Memanggil fetch() untuk memuat ulang data terbaru dan menyelaraskan detailsRef
   * - Membuka modal error jika gagal atau barcode tidak ditemukan
   */
  const handleAddUnit = (eOrBarcode) => {
    if (eOrBarcode && typeof eOrBarcode.preventDefault === 'function') {
      eOrBarcode.preventDefault();
    }

    const rawValue = typeof eOrBarcode === 'string' ? eOrBarcode : qrCode;
    const value = rawValue ? rawValue.trim().replace(/['"`]/g, "") : "";
    if (!value) return;

    if (typeof eOrBarcode !== 'string') {
      setQrCode("");
    }

    return enqueueTransferAction(async () => {
      setIsAddingUnit(true);
      try {
        // 1. Coba cari barcode sebagai unit fisik unik
        let productCode = null;
        try {
          const response = await api.productCode.index({ qr_code: value });
          productCode = response?.data?.[0];
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
        }

        if (productCode) {
          const isAlreadyAdded = detailsRef.current.some(
            (d) => d.product_code_id === productCode.id
          );
          if (isAlreadyAdded) {
            setQrCode("");
            showError(
              "Unit Sudah Ditambahkan",
              null,
              "Unit fisik dengan barcode ini sudah ada di dalam daftar transfer gudang."
            );
            return;
          }

          await api.warehouseTransfer.addDetail(id, {
            product_variant_id: productCode.product_variant_id,
            product_code_id: productCode.id,
          });
          setQrCode("");
          setExpandedRows((prev) => ({
            ...prev,
            [productCode.product_variant_id]: true,
          }));
          await fetch();
          return;
        }

        // 2. Fallback: Coba cari barcode sebagai varian umum (per kuantitas)
        let variant = null;
        try {
          const variantResponse = await api.productVariant.index({ qr_code: value });
          variant = variantResponse?.data?.[0];

          if (variant) {
            const isUnique = await checkIsUniqueBarcode(variant);
            if (isUnique) {
              setQrCode("");
              showError(
                "Barcode Tidak Valid",
                null,
                "Produk ini menggunakan barcode unik. Silakan scan barcode unik pada unit fisik barang."
              );
              return;
            }
          }
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
        }

        // 3. Fallback: Coba cari barcode sebagai kode produk (misal: barang umum / kresek)
        if (!variant) {
          try {
            const productResponse = await api.product.index({ code: value });
            const product = productResponse?.data?.[0];

            if (product) {
              const isUnique = await checkIsUniqueBarcode(null, product);
              if (isUnique) {
                setQrCode("");
                showError(
                  "Barcode Tidak Valid",
                  null,
                  "Produk ini menggunakan barcode unik. Silakan scan barcode unik pada unit fisik barang."
                );
                return;
              }

              const variants =
                product?.ProductVariants ?? product?.product_variants ?? [];
              if (variants.length === 1) {
                variant = variants[0];
              } else if (variants.length > 1) {
                setQrCode("");
                showError(
                  "Varian Produk Ambigu",
                  null,
                  "Produk ini memiliki lebih dari 1 varian. Silakan gunakan tombol 'Cari Produk' untuk memilih varian yang tepat."
                );
                return;
              }
            }
          } catch (err) {
            if (err?.response?.status !== 404) throw err;
          }
        }

        if (variant) {
          const existingDetail = detailsRef.current.find(
            (d) => d.product_variant_id === variant.id && !d.product_code_id
          );

          if (existingDetail) {
            await api.warehouseTransfer.updateDetail(id, existingDetail.id, {
              qty: Number(existingDetail.qty) + 1,
            });
          } else {
            await api.warehouseTransfer.addDetail(id, {
              product_variant_id: variant.id,
              qty: 1,
            });
          }
          setQrCode("");
          await fetch();
          return;
        }

        // 4. Jika tidak ditemukan di unit maupun varian
        setQrCode("");
        showError(
          "Transfer Gudang",
          null,
          "Barcode tidak ditemukan pada unit fisik maupun varian produk."
        );
      } catch (err) {
        setQrCode("");
        showError(
          "Transfer Gudang",
          err,
          "Gagal menambahkan item via barcode."
        );
      } finally {
        setIsAddingUnit(false);
      }
    });
  };

  /**
   * Menangani pemindaian barcode hardware global untuk halaman Detail Transfer Gudang.
   * Aktif secara otomatis saat transfer berstatus draft dan modal pemilihan produk tidak terbuka.
   */
  useBarcodeScanner(
    (barcode) => {
      if (isDraft && !showStates?.["product.choose"]) {
        handleAddUnit(barcode);
      }
    },
    { info: "Transfer Gudang", active: Boolean(isDraft && !showStates?.["product.choose"]) }
  );

  /**
   * Menangani perubahan kuantitas item varian umum saat input kehilangan fokus (onBlur).
   * Nilai divalidasi harus angka valid dan lebih besar dari 0.
   *
   * @param {Object} detail - Objek detail transfer atau grup item
   * @param {number} detail.id - ID detail transfer
   * @param {number} detail.qty - Kuantitas saat ini sebelum diedit
   * @param {React.FocusEvent<HTMLInputElement>} e - Event blur dari elemen input
   * @returns {Promise<void>} Tidak mengembalikan nilai
   * @sideEffects
   * - Mengubah state savingDetailId
   * - Memanggil API updateDetail
   * - Memanggil fetch() untuk memuat ulang data
   * - Mengembalikan nilai input ke qty awal jika validasi gagal atau request error
   */
  const handleQtyBlur = async (detail, e) => {
    const raw = e.target.value;
    const nextQty = Number(raw);
    if (!raw || !Number.isFinite(nextQty) || nextQty <= 0) {
      e.target.value = detail?.qty;
      return;
    }
    if (nextQty === detail?.qty) return;

    setSavingDetailId(detail?.id);
    try {
      await api.warehouseTransfer.updateDetail(id, detail?.id, {
        qty: nextQty,
      });
      await fetch();
    } catch (err) {
      showError("Transfer Gudang", err, "Gagal memperbarui jumlah.");
      e.target.value = detail?.qty;
    } finally {
      setSavingDetailId(null);
    }
  };

  /**
   * Menghapus satu unit fisik spesifik berdasarkan ID detail transfer.
   *
   * @param {number|string} detailId - ID baris detail transfer gudang
   * @returns {Promise<void>} Tidak mengembalikan nilai
   * @sideEffects
   * - Menampilkan dialog konfirmasi penghapusan unit
   * - Mengubah state deletingUnitId
   * - Memanggil API api.warehouseTransfer.deleteDetail
   * - Memanggil fetch() untuk memuat ulang data
   */
  const handleDeleteDetail = async (detailId) => {
    if (!(await confirm("Apakah Anda yakin ingin menghapus unit ini?"))) return;
    setDeletingUnitId(detailId);
    try {
      await api.warehouseTransfer.deleteDetail(id, detailId);
      await fetch();
    } catch (err) {
      showError("Transfer Gudang", err, "Gagal menghapus unit.");
    } finally {
      setDeletingUnitId(null);
    }
  };

  /**
   * Menghapus seluruh detail item dalam satu baris grup (termasuk semua unit fisik jika digrouping).
   * Menggunakan Promise.allSettled agar semua penghapusan dijalankan secara tuntas dan
   * memberikan pesan error yang spesifik jika ada sebagian unit yang gagal dihapus.
   *
   * @param {Object} group - Objek baris grup item
   * @param {Array<number>} group.detailIds - Daftar ID detail yang akan dihapus
   * @returns {Promise<void>} Tidak mengembalikan nilai
   * @sideEffects
   * - Menampilkan dialog konfirmasi penghapusan
   * - Mengubah state deletingDetailId
   * - Memanggil API api.warehouseTransfer.deleteDetail untuk setiap ID detail
   * - Memanggil fetch() untuk memuat ulang data
   * - Menampilkan modal error jika terjadi kegagalan sebagian atau seluruh penghapusan
   */
  const handleDeleteGroup = async (group) => {
    if (!(await confirm("Apakah Anda yakin ingin menghapus item ini?"))) return;
    setDeletingDetailId(group?.id);
    try {
      const results = await Promise.allSettled(
        group.detailIds.map((detailId) =>
          api.warehouseTransfer.deleteDetail(id, detailId)
        )
      );
      await fetch();

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        showError(
          "Transfer Gudang",
          null,
          `${failed.length} dari ${group.detailIds.length} unit gagal dihapus.`
        );
      }
    } catch (err) {
      showError("Transfer Gudang", err, "Gagal menghapus item.");
    } finally {
      setDeletingDetailId(null);
    }
  };

  const handleShip = async () => {
    if (!(await confirm("Apakah Anda yakin ingin mengirim transfer " + warehouseTransfer?.code + "?")))
      return;
    setIsShipping(true);
    try {
      await api.warehouseTransfer.ship(id);
      await fetch();
    } catch (err) {
      showError("Transfer Gudang", err, "Gagal mengirim transfer.");
    } finally {
      setIsShipping(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirm("Apakah Anda yakin ingin membatalkan transfer " + warehouseTransfer?.code + "?")))
      return;
    setIsCanceling(true);
    try {
      await api.warehouseTransfer.cancel(id);
      await fetch();
    } catch (err) {
      showError("Transfer Gudang", err, "Gagal membatalkan transfer.");
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <MainLayout
      activeSidebarNavigation="warehouse"
      activeSidebarChild="warehouse-transfer"
      breadcrumbItems={breadcrumbItems}
      pageTitle={warehouseTransfer?.code ?? "Detail Transfer"}
      headingButtons={
        isDraft
          ? [
            {
              label: "Pilih Item",
              type: "button",
              onClick: () => {
                setPayloads("product.choose");
                openModal("product.choose");
              },
            },
          ]
          : []
      }
    >
      <AppModals
        items={["errorMessage.view", "product.choose"]}
        onSuccess={{
          "errorMessage.view": () => { },
          "product.choose": (data) => handleSelectItem(data),
        }}
      />

      <FormSummary className="w-full">
        <Table.Wrapper asIndex={false}>
          <Table.Body>
            <Table.BodyRow>
              <Table.Cell>Kode</Table.Cell>
              <Table.Cell>: {warehouseTransfer?.code}</Table.Cell>
              <Table.Cell colSpan={2}></Table.Cell>
              <Table.Cell>Status</Table.Cell>
              <Table.Cell>
                <Badge color={statusBadgeColor(status)}>
                  {statusLabels[status] ?? status}
                </Badge>
              </Table.Cell>
            </Table.BodyRow>
            <Table.BodyRow>
              <Table.Cell>Gudang Asal</Table.Cell>
              <Table.Cell>
                :{" "}
                {warehouseLabel(
                  warehouseTransfer?.source_warehouse_id,
                  warehouses
                )}
              </Table.Cell>
              <Table.Cell colSpan={2}></Table.Cell>
              <Table.Cell>Gudang Tujuan</Table.Cell>
              <Table.Cell>
                :{" "}
                {warehouseLabel(
                  warehouseTransfer?.dest_warehouse_id,
                  warehouses
                )}
              </Table.Cell>
            </Table.BodyRow>
            <Table.BodyRow>
              <Table.Cell>Catatan</Table.Cell>
              <Table.Cell colSpan={5}>
                : {warehouseTransfer?.notes || "-"}
              </Table.Cell>
            </Table.BodyRow>
          </Table.Body>
        </Table.Wrapper>
      </FormSummary>

      {isDraft && (
        <FormHeader appearance="none">
          <div className="sm:col-span-2 md:col-span-2 lg:col-span-2">
            <InputLabel>Tambah Unit via Barcode</InputLabel>
            <form className="flex gap-2" onSubmit={handleAddUnit}>
              <div className="flex-grow">
                <Input testId="warehouse-transfer-detail-qr-code-input"
                  type="text"
                  name="qr_code"
                  placeholder="Scan / ketik QR Code unit"
                  value={qrCode}
                  onChange={(e) => setQrCode(e.target.value.replace(/['"`]/g, ""))}
                />
              </div>
              <div className="w-32">
                <Button testId="warehouse-transfer-detail-tambah-button" type="submit" loading={isAddingUnit}>
                  Tambah
                </Button>
              </div>
            </form>
          </div>
        </FormHeader>
      )}

      <Table.Container>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Item</Table.Heading>
              <Table.Heading>Ukuran</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              <Table.Heading>Qty</Table.Heading>
              <Table.Heading>Unit</Table.Heading>
              {isDraft && <Table.Heading>Aksi</Table.Heading>}
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {groupedDetails?.length > 0 ? (
              groupedDetails.map((group) => {
                const rowKey = group?.product_variant_id || group?.id;
                const isExpanded = Boolean(expandedRows[rowKey]);

                return (
                  <React.Fragment
                    key={
                      group?.isUnique
                        ? `unique-${group?.product_variant_id}`
                        : group?.id
                    }
                  >
                    <Table.BodyRow>
                      <Table.Cell>
                        {group?.variant?.product_detail?.name ?? "-"}
                      </Table.Cell>
                      <Table.Cell>{group?.variant?.size?.name ?? "-"}</Table.Cell>
                      <Table.Cell>{group?.variant?.color?.name ?? "-"}</Table.Cell>
                      <Table.Cell>
                        {isDraft && !group?.isUnique ? (
                          <input data-testid={`warehouse-transfer-detail-item-input-${group?.id + "-" + group?.qty}`}
                            key={group?.id + "-" + group?.qty}
                            className="w-20 rounded-lg border border-gray-300 bg-white p-1.5 text-gray-900 shadow-sm focus:border-primary-600 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-600"
                            type="number"
                            min="1"
                            defaultValue={group?.qty}
                            disabled={savingDetailId === group?.id}
                            onBlur={(e) => handleQtyBlur(group?.rawDetail || group, e)}
                          />
                        ) : (
                          group?.qty
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {group?.isUnique ? (
                          <button data-testid="warehouse-transfer-detail-chevron-up-button"
                            type="button"
                            onClick={() => toggleRow(rowKey)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-50 hover:bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 text-xs font-medium cursor-pointer transition-colors shadow-2xs border border-primary-200 dark:border-primary-800"
                            title={isExpanded ? "Tutup rincian barcode" : "Buka rincian barcode"}
                          >
                            <span>{group.unitCodes.length} Unit</span>
                            {isExpanded ? (
                              <ChevronUpIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDownIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : (
                          "-"
                        )}
                      </Table.Cell>
                      {isDraft && (
                        <Table.Cell>
                          {deletingDetailId === group?.id ? (
                            <span className="text-xs text-gray-400">
                              Menghapus...
                            </span>
                          ) : (
                            <IconButton testId="warehouse-transfer-detail-trash-button"
                              icon={TrashIcon}
                              color="red"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteGroup(group);
                              }}
                            />
                          )}
                        </Table.Cell>
                      )}
                    </Table.BodyRow>
                    {group?.isUnique && isExpanded && (
                      <Table.BodyRow className="bg-gray-50/60 dark:bg-gray-800/40">
                        <Table.Cell
                          colSpan={isDraft ? 6 : 5}
                          className="py-3 px-6"
                        >
                          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 shadow-xs">
                            <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-gray-100 dark:border-gray-800">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                Daftar Barcode Unit Fisik ({group.unitCodes.length} unit)
                              </span>
                              <span className="text-xs text-gray-400">
                                {isDraft
                                  ? "Klik tombol × untuk menghapus unit tertentu"
                                  : "Rincian unit barcode yang dipindahkan"}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {group.unitCodes.map((u, idx) => (
                                <div
                                  key={u.id}
                                  className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-xs font-mono text-gray-800 dark:text-gray-200 shadow-2xs"
                                >
                                  <span className="truncate" title={u.qr_code}>
                                    <span className="text-gray-400 font-sans mr-1.5">
                                      {idx + 1}.
                                    </span>
                                    {u.qr_code}
                                  </span>
                                  {isDraft && (
                                    <button data-testid="warehouse-transfer-detail-hapus-unit-ini-button"
                                      type="button"
                                      className="text-gray-400 hover:text-red-500 font-bold ml-2 cursor-pointer p-0.5 disabled:cursor-wait disabled:hover:text-gray-400"
                                      disabled={deletingUnitId === u.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteDetail(u.id);
                                      }}
                                      title="Hapus unit ini"
                                    >
                                      {deletingUnitId === u.id ? "..." : <>&times;</>}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </Table.Cell>
                      </Table.BodyRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <Table.BodyRow>
                <Table.Cell
                  colSpan={isDraft ? 6 : 5}
                  className="text-center text-gray-500 py-4"
                >
                  Belum ada item.
                </Table.Cell>
              </Table.BodyRow>
            )}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>

      {(isDraft || status === "in_transit") && (
        <div className="flex justify-end gap-2 px-2 py-4">
          {isDraft && (
            <BadgeButton testId="warehouse-transfer-detail-kirim-button"
              color="green"
              useLoadingState={true}
              loading={isShipping}
              onClick={handleShip}
            >
              Kirim
            </BadgeButton>
          )}
          <BadgeButton testId="warehouse-transfer-detail-batal-button"
            color="red"
            useLoadingState={true}
            loading={isCanceling}
            onClick={handleCancel}
          >
            Batal
          </BadgeButton>
        </div>
      )}
      {ConfirmDialog}
    </MainLayout>
  );
}

export default WarehouseTransferDetail;
